import React from 'react';
import VisaPageLayout, { FaqItem, ProcessStep, RequirementItem } from './visa-page-layout';
import { useVisaPacksByCountry } from '../hooks/useVisaPacksByCountry';


// NOTE ON CONTENT: requirements, "good to know" copy, steps and FAQs below
// stay hardcoded editorial content for the Oman page — only the visa
// plans/pricing now come live from the API (VisaPack records where
// destination_country = Oman), via useVisaPacksByCountry('OM').

const requirements: RequirementItem[] = [
  { label: 'Passport copy', detail: 'Valid for at least 6 months from your arrival date, with at least one blank page.' },
  { label: 'Passport-size photo', detail: 'Recent, plain white background, matching standard visa photo specifications.' },
  { label: 'Confirmed return ticket', detail: 'A round-trip or onward flight booking showing your planned departure from Oman.' },
  { label: 'Hotel or host details', detail: 'Confirmed accommodation booking, or a sponsor letter if staying with family or friends.' },
  { label: 'Proof of funds (if requested)', detail: 'A recent bank statement may be requested for certain nationalities during review.' },
];

const goodToKnow: string[] = [
  'Most nationalities are eligible for the tourist e-visa; a short list of countries requires a different route — we\'ll flag this during your application.',
  'Your passport must be valid for the entire length of your planned stay, plus 6 months.',
  'Processing times are business days and can extend during public holidays in Oman.',
  'We review your documents before submission, so incomplete files rarely reach the immigration system.',
];

const steps: ProcessStep[] = [
  { title: 'Upload your documents', text: 'Submit your passport scan, photo and travel details through our secure form.' },
  { title: 'We review & submit', text: 'Our visa team checks everything against current requirements, then files your application.' },
  { title: 'Track your status', text: 'Get updates by WhatsApp or email while the Royal Oman Police process your request.' },
  { title: 'Receive your eVisa', text: 'Download the approved eVisa as a PDF — print a copy to carry with your passport.' },
];

const faqs: FaqItem[] = [
  { question: 'How long before my trip should I apply?', answer: 'We recommend applying at least 7–10 days before departure to leave room for standard processing and any additional document requests.' },
  { question: 'Can I extend my Oman tourist visa once I arrive?', answer: 'Extensions are possible in some cases through the Royal Oman Police directly. We can guide you through that process if your plans change.' },
  { question: 'Do children need their own visa?', answer: 'Yes — every traveler, including infants and children, needs an individual eVisa with their own passport.' },
  { question: 'What if my application is rejected?', answer: 'We review documents closely before submission specifically to avoid this, and our team will help you understand and address the reason if it happens.' },
];

export default function VisaOmanPage() {
  const { plans, loading, error } = useVisaPacksByCountry('OMN');

  return (
    <VisaPageLayout
      countryName="Oman"
      flagEmoji="🇴🇲"
      heroEyebrow="Sultanate of Oman"
      heroImage="https://images.unsplash.com/photo-1599743777555-e362a2feab39?w=1600&q=80&fit=crop&crop=center"
      heroTitleLead="Your"
      heroTitleHighlight="Oman"
      heroTitleTail="tourist visa, handled end to end"
      heroSubtitle="From Muscat's corniche to the dunes of the Sharqiya Sands — we prepare and file your Oman eVisa so the only thing you need to plan is the trip itself."
      trustStats={[
        { value: '2–7', label: 'Business Days' },
        { value: '98%', label: 'Approval Rate' },
        { value: '4,900+', label: 'Oman Visas Issued' },
      ]}
      plans={plans}
      plansLoading={loading}
      plansError={error}
      requirements={requirements}
      goodToKnow={goodToKnow}
      steps={steps}
      faqs={faqs}
      ctaSubtitle="Upload your documents today and our team will take it from here — most applications are filed within one business day of a complete submission."
    />
  );
}